/**
 * Screening quantity takeoffs for a generation parcel test fit.
 *
 * Pure projection over solver output (or a frozen exhibit fit via the
 * adapter) — derived at render time and never persisted, so hash-validated
 * exhibit content is untouched. Quantities are planning-grade: pads use the
 * deterministic screening rectangles, the fence line is the required
 * envelope, and circulation is the fenced area not covered by components
 * (which inherently includes component gaps and road corridors). Civil
 * design, grading and BOQ-grade quantities remain external.
 */
import type {
  GenerationLayoutExhibitFit,
} from '../../types/generationImpact';
import {
  localAxesForPoint,
  type GenerationParcelTestFitResult,
} from './generationLayoutGeometry';
import {
  generationComponentGroundRingM,
  occupiedGenerationGroundAcres,
} from './generationLayoutYield';
import { SQUARE_METRES_PER_ACRE, type GenerationTestFitComponentKind } from './generationTestFit';

export interface GenerationLayoutTakeoffLine {
  id: string;
  label: string;
  quantity: number;
  unit: 'acres' | 'm' | 'count';
  basis: string;
}

export interface GenerationLayoutTakeoffs {
  status: 'available' | 'unavailable';
  reason?: string;
  lines: GenerationLayoutTakeoffLine[];
  totals: {
    fencedAreaAcres: number;
    fencePerimeterM: number;
    permanentPadAcres: number;
    circulationAcres: number;
    routeReservationLengthM: number;
    routeReservationAcres: number;
    constructionLaydownAcres: number;
    excludedGroundAcres: number | null;
  } | null;
}

interface TakeoffComponent {
  id?: string;
  kind: GenerationTestFitComponentKind;
  areaAcres: number;
  widthM: number;
  heightM: number;
  /** Exact source centerline length for drawn/imported road corridors. */
  exactCenterlineLengthM?: number;
  lifecycle?: 'permanent' | 'route-reservation' | 'construction';
}

interface TakeoffFitInput {
  components: readonly TakeoffComponent[];
  requiredEnvelope: { widthM: number; heightM: number } | null;
  excludedAcres: number | null;
  occupiedGroundAcres?: number | null;
}

const KIND_LABELS: Record<GenerationTestFitComponentKind, string> = {
  'generation-block': 'Generation block pads',
  cooling: 'Cooling system pad',
  switchyard: 'Switchyard pad',
  'ccs-capture': 'CCS capture pad',
  'ccs-compression': 'CCS compression pad',
  'natural-gas-route': 'Natural-gas route reservation',
  'co2-export-route': 'CO₂ export route reservation',
  'construction-laydown': 'Construction laydown',
  'access-road': 'Access-road corridors',
  road: 'Roads',
  parking: 'Parking areas',
  building: 'Buildings',
  'data-center': 'Data-center pads',
  generator: 'Generator pads',
  custom: 'Custom components',
};

const KIND_ORDER: readonly GenerationTestFitComponentKind[] = [
  'generation-block',
  'cooling',
  'switchyard',
  'ccs-capture',
  'ccs-compression',
  'natural-gas-route',
  'co2-export-route',
  'construction-laydown',
  'access-road',
  'road',
  'parking',
  'building',
  'data-center',
  'generator',
  'custom',
];

function round(value: number, digits: number): number {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

function compute(input: TakeoffFitInput): GenerationLayoutTakeoffs {
  if (!input.requiredEnvelope || input.components.length === 0) {
    return {
      status: 'unavailable',
      reason: 'No packed layout is available to take off.',
      lines: [],
      totals: null,
    };
  }

  const lines: GenerationLayoutTakeoffLine[] = [];
  for (const kind of KIND_ORDER) {
    const matching = input.components.filter(component => component.kind === kind);
    if (matching.length === 0) continue;
    const areaAcres = matching.reduce((sum, component) => sum + component.areaAcres, 0);
    if (kind === 'generation-block') {
      lines.push({
        id: 'generation-block-count',
        label: 'Generation blocks',
        quantity: matching.length,
        unit: 'count',
        basis: 'placed screening rectangles',
      });
    }
    if (kind === 'natural-gas-route' || kind === 'co2-export-route') {
      const lengthM = matching.reduce((sum, component) => (
        sum + Math.max(component.widthM, component.heightM)
      ), 0);
      lines.push({
        id: `${kind}-length`,
        label: `${KIND_LABELS[kind]} length`,
        quantity: round(lengthM, 0),
        unit: 'm',
        basis: 'reserved corridor rectangle',
      });
    }
    if (kind === 'access-road' || kind === 'road') {
      const lengthM = matching.reduce((sum, component) => (
        sum + (
          component.exactCenterlineLengthM
          ?? Math.max(component.widthM, component.heightM)
        )
      ), 0);
      lines.push({
        id: `${kind}-length`,
        label: `${KIND_LABELS[kind]} length`,
        quantity: round(lengthM, 0),
        unit: 'm',
        basis: kind === 'access-road'
          ? 'row corridor rectangle span'
          : matching.every(component => component.exactCenterlineLengthM != null)
            ? 'exact custom centerline'
            : 'exact custom centerline + screening span',
      });
    }
    lines.push({
      id: `${kind}-area`,
      label: KIND_LABELS[kind],
      quantity: round(areaAcres, 2),
      unit: 'acres',
      basis: 'placed screening rectangles',
    });
  }

  const fencedAreaM2 = input.requiredEnvelope.widthM * input.requiredEnvelope.heightM;
  const fencedAreaAcres = fencedAreaM2 / SQUARE_METRES_PER_ACRE;
  const fencePerimeterM = 2 * (input.requiredEnvelope.widthM + input.requiredEnvelope.heightM);
  const permanentPadAcres = input.components
    .filter(component => (
      (component.lifecycle ?? 'permanent') === 'permanent'
      && component.kind !== 'road'
      && component.kind !== 'access-road'
    ))
    .reduce((sum, component) => sum + component.areaAcres, 0);
  const routeComponents = input.components
    .filter(component => component.lifecycle === 'route-reservation');
  const routeReservationAcres = routeComponents
    .reduce((sum, component) => sum + component.areaAcres, 0);
  const routeReservationLengthM = routeComponents
    .reduce((sum, component) => sum + Math.max(component.widthM, component.heightM), 0);
  const constructionLaydownAcres = input.components
    .filter(component => component.lifecycle === 'construction')
    .reduce((sum, component) => sum + component.areaAcres, 0);
  const totalOccupiedAcres = input.occupiedGroundAcres
    ?? input.components.reduce((sum, component) => sum + component.areaAcres, 0);
  const circulationAcres = Math.max(0, fencedAreaAcres - totalOccupiedAcres);

  lines.push(
    {
      id: 'fence-perimeter',
      label: 'Security fence line',
      quantity: round(fencePerimeterM, 0),
      unit: 'm',
      basis: 'required envelope perimeter',
    },
    {
      id: 'circulation-area',
      label: 'Circulation, gaps & road corridors inside fence',
      quantity: round(circulationAcres, 2),
      unit: 'acres',
      basis: 'fenced area minus component pads',
    },
    {
      id: 'disturbed-area',
      label: 'Disturbed area (fenced envelope)',
      quantity: round(fencedAreaAcres, 2),
      unit: 'acres',
      basis: 'required envelope including setbacks',
    },
  );

  return {
    status: 'available',
    lines,
    totals: {
      fencedAreaAcres: round(fencedAreaAcres, 2),
      fencePerimeterM: round(fencePerimeterM, 0),
      permanentPadAcres: round(permanentPadAcres, 2),
      circulationAcres: round(circulationAcres, 2),
      routeReservationLengthM: round(routeReservationLengthM, 0),
      routeReservationAcres: round(routeReservationAcres, 2),
      constructionLaydownAcres: round(constructionLaydownAcres, 2),
      excludedGroundAcres: input.excludedAcres == null ? null : round(input.excludedAcres, 2),
    },
  };
}

function expandedTakeoffEnvelope(
  testFit: GenerationParcelTestFitResult,
): { widthM: number; heightM: number } | null {
  const required = testFit.plan?.requiredEnvelope;
  if (!required) return null;
  const setbackM = Number.isFinite(testFit.layout.edgeSetbackM)
    ? Math.max(0, testFit.layout.edgeSetbackM)
    : 0;
  let minX = -required.widthM / 2;
  let maxX = required.widthM / 2;
  let minY = -required.heightM / 2;
  let maxY = required.heightM / 2;
  for (const component of testFit.components) {
    for (const point of generationComponentGroundRingM(component)) {
      if (!Number.isFinite(point.xM) || !Number.isFinite(point.yM)) continue;
      minX = Math.min(minX, point.xM - setbackM);
      maxX = Math.max(maxX, point.xM + setbackM);
      minY = Math.min(minY, point.yM - setbackM);
      maxY = Math.max(maxY, point.yM + setbackM);
    }
  }
  return {
    widthM: Math.max(required.widthM, maxX - minX),
    heightM: Math.max(required.heightM, maxY - minY),
  };
}

function exactCustomRoadLengths(
  testFit: GenerationParcelTestFitResult,
): ReadonlyMap<string, number> {
  const lengths = new Map<string, number>();
  for (const custom of testFit.layout.customComponents ?? []) {
    if (custom.shape.type !== 'polyline' || custom.shape.path.length < 2) continue;
    const local = custom.shape.path.map(point => (
      localAxesForPoint(point, testFit.anchor, testFit.layout.rotationDeg)
    ));
    const lengthM = local.slice(1).reduce((sum, point, index) => {
      const prior = local[index]!;
      return sum + Math.hypot(point.xM - prior.xM, point.yM - prior.yM);
    }, 0);
    if (!(lengthM > 0) || !Number.isFinite(lengthM)) continue;
    const prefix = `custom-${custom.id}`;
    const generatedIds = testFit.components
      .map(component => component.rectangle.id)
      .filter(id => id === prefix || id.startsWith(`${prefix}-`))
      .sort();
    generatedIds.forEach((id, index) => {
      // A self-splitting corridor may produce more than one polygon. Its
      // source centerline is still one road and is counted exactly once.
      lengths.set(id, index === 0 ? lengthM : 0);
    });
  }
  return lengths;
}

export function computeGenerationLayoutTakeoffs(
  testFit: GenerationParcelTestFitResult,
): GenerationLayoutTakeoffs {
  const exactRoadLengths = exactCustomRoadLengths(testFit);
  return compute({
    components: testFit.components.map(component => ({
      id: component.rectangle.id,
      kind: component.rectangle.kind,
      areaAcres: component.rectangle.areaAcres,
      widthM: component.rectangle.widthM,
      heightM: component.rectangle.heightM,
      ...(exactRoadLengths.has(component.rectangle.id)
        ? { exactCenterlineLengthM: exactRoadLengths.get(component.rectangle.id)! }
        : {}),
      lifecycle: component.rectangle.lifecycle,
    })),
    requiredEnvelope: expandedTakeoffEnvelope(testFit),
    excludedAcres: testFit.usable?.excludedAcres ?? null,
    occupiedGroundAcres: occupiedGenerationGroundAcres(testFit),
  });
}

/** Same takeoffs from a frozen exhibit fit (report rendering path). */
export function computeTakeoffsFromExhibitFit(
  fit: GenerationLayoutExhibitFit,
): GenerationLayoutTakeoffs {
  return compute({
    components: fit.components.map(component => ({
      kind: component.kind,
      areaAcres: component.areaAcres,
      widthM: component.widthM,
      heightM: component.heightM,
      lifecycle: component.lifecycle,
    })),
    requiredEnvelope: fit.plan
      ? {
          widthM: fit.plan.requiredEnvelope.widthM,
          heightM: fit.plan.requiredEnvelope.heightM,
        }
      : null,
    excludedAcres: fit.usable?.excludedAcres ?? null,
  });
}
